import path from 'path';
import { Prompt } from 'react-router-dom';
import PropTypes from 'prop-types';
import React, { useEffect, useCallback, useMemo } from 'react';
import { createHashHistory } from 'history';
import ContractSpecAmbientConditions from './contractSpecAmbientConditions';
import ContractSpecContainer from './contractSpecContainer';
import ContractSpecDryFilm from './contractSpecDryFilm';
import ContractSpecGeneral from './contractSpecGeneral';
import ContractSpecSaltTest from './contractSpecSaltTest';
import ContractSpecSurfaceProfile from './contractSpecSurfaceProfile';
import ContractSpecWetFilm from './contractSpecWetFilm';
import entityComposition from '../../common/classes/entityComposition';
import { editPermissionSelector, viewPermissionSelector } from '../../common/classes/permissions';
import routeHelper from '../../common/classes/routeHelper';
import Loader from '../../common/loader';
import SectionPanel from '../../common/containers/sectionPanel';
import useLookup from '../../../utils/useLookup';
const hashHistory = createHashHistory();
const handleBack = () => {
  hashHistory.replace('/admin/contractSpecs');
};
const ContractSpec = props => {
  const canView = props.canView,
    location = props.location,
    login = props.login,
    canEdit = props.canEdit,
    _props$contractSpec = props.contractSpec,
    active = _props$contractSpec.active,
    pendingChanges = _props$contractSpec.pendingChanges,
    loading = _props$contractSpec.loading,
    match = props.match,
    jobAndPhaseCombinations = props.jobAndPhaseCombinations,
    _props$contractSpecAc = props.contractSpecActions,
    serverShow = _props$contractSpecAc.serverShow,
    change = _props$contractSpecAc.change,
    serverSave = _props$contractSpecAc.serverSave,
    errorMessage = _props$contractSpecAc.errorMessage,
    serverIndex = props.contractSpecFilterContractSpecActions.serverIndex,
    jobAndPhaseInitialIndex = props.jobAndPhaseInitialIndex;
  const pcLabel = useLookup('Phase Code').toLowerCase();
  // Handle the logic to redirect
  useEffect(() => {
    routeHelper.handleViewRedirect(canView, location);
  }, [canView, location]);

  // Lookup job/phase codes and fetch the current contract spec
  // if one exists
  useEffect(() => {
    serverIndex(jobAndPhaseInitialIndex);
    if (!active.isNew || active.id !== match.params.id) {
      serverShow(match.params.id);
    }
    /**
     * This is a bit of a workaround to get this working with our
     * routeHelper which was built for class components.  We need to
     * not trigger a rerender on active.id or active.new since
     * active is cleared out before the route transitions in the helper.
     * This wasn't a problem for the component lifecycle methods since
     * they were only ran on component mount, these are checked every
     * cycle.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverShow, jobAndPhaseInitialIndex, match.params.id, serverIndex]);
  const handleChange = useCallback((contentPath, value) => change(active.id, contentPath, value), [active.id, change]);
  const validate = useCallback(() => {
    const errors = [];
    const nestedActive = active.active,
      jobID = active.jobID,
      jobPhaseCodeID = active.jobPhaseCodeID,
      id = active.id;
    // Remove the current selection from the pairings before checking for duplicates
    const jobAndPhaseCodes = (jobAndPhaseCombinations || []).filter(combo => combo.id !== id);
    if (!jobID) {
      errors.push('The Job Number is required.');
    }
    if (jobPhaseCodeID && nestedActive) {
      const doesComboExist = jobAndPhaseCodes.some(combo => combo.jobID === jobID && combo.phaseID === jobPhaseCodeID);
      if (doesComboExist) {
        errors.push("The combination of job number, ".concat(pcLabel, " must be unique."));
      }
    }
    if (jobPhaseCodeID === null && nestedActive) {
      const nullExists = jobAndPhaseCodes.some(combo => combo.phaseID === null && combo.jobID === jobID);
      if (nullExists) {
        errors.push("You cannot have more then one contract spec without a ".concat(pcLabel, " for each job."));
      }
    }
    return errors;
  }, [active, jobAndPhaseCombinations, pcLabel]);
  const handleSave = useCallback(() => {
    const errors = validate();
    if (errors.length > 0) {
      errorMessage(errors);
    } else {
      serverSave(active);
    }
  }, [active, errorMessage, serverSave, validate]);
  const section = match.params.section || 'general';
  const forms = useMemo(() => ({
    general: /*#__PURE__*/React.createElement(ContractSpecGeneral, {
      active: active,
      onChange: handleChange,
      userID: login.user.user_id
    }),
    ambientConditions: /*#__PURE__*/React.createElement(ContractSpecAmbientConditions, {
      active: active,
      onChange: handleChange,
      userID: login.user.user_id
    }),
    surfaceProfile: /*#__PURE__*/React.createElement(ContractSpecSurfaceProfile, {
      active: active,
      onChange: handleChange,
      userID: login.user.user_id
    }),
    dryFilmThickness: /*#__PURE__*/React.createElement(ContractSpecDryFilm, {
      active: active,
      onChange: handleChange,
      userID: login.user.user_id
    }),
    wetFilmThickness: /*#__PURE__*/React.createElement(ContractSpecWetFilm, {
      active: active,
      onChange: handleChange,
      userID: login.user.user_id
    }),
    saltTest: /*#__PURE__*/React.createElement(ContractSpecSaltTest, {
      active: active,
      onChange: handleChange,
      userID: login.user.user_id
    })
  }), [active, handleChange, login.user.user_id]);
  const subsection = forms[section];
  const handleTransition = useCallback(async calledLocation => {
    const transitionProps = {
      handleSave,
      props
    };
    const sectionRoutes = Object.keys(forms).map(formKey => path.join('/admin/contractSpecs/', match.params.id, formKey));
    routeHelper.transitionAsync(transitionProps, 'contractSpec', calledLocation, sectionRoutes);
  }, [forms, handleSave, match.params.id, props]);
  return /*#__PURE__*/React.createElement(ContractSpecContainer, {
    section: section,
    id: match.params.id
  }, /*#__PURE__*/React.createElement(Prompt, {
    when: pendingChanges,
    message: handleTransition
  }), /*#__PURE__*/React.createElement(SectionPanel, {
    isPending: pendingChanges,
    isSaveDisabled: loading || !pendingChanges,
    onBack: handleBack,
    onSave: handleSave,
    name: "Contract Spec",
    showSave: canEdit
  }, /*#__PURE__*/React.createElement(Loader, {
    loaded: !loading
  }, subsection)));
};
ContractSpec.propTypes = {
  canEdit: PropTypes.bool.isRequired,
  canView: PropTypes.bool.isRequired,
  jobAndPhaseInitialIndex: PropTypes.shape({}).isRequired,
  jobAndPhaseCombinations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    jobID: PropTypes.string,
    phaseID: PropTypes.string
  })).isRequired,
  contractSpecFilterContractSpecActions: PropTypes.shape({
    serverIndex: PropTypes.func
  }).isRequired,
  contractSpec: PropTypes.shape({
    active: PropTypes.shape({
      active: PropTypes.number,
      isNew: PropTypes.bool,
      id: PropTypes.string,
      jobID: PropTypes.string,
      jobPhaseCodeID: PropTypes.string
    }),
    loading: PropTypes.bool,
    pendingChanges: PropTypes.bool
  }).isRequired,
  contractSpecActions: PropTypes.shape({
    showCancel: PropTypes.func,
    change: PropTypes.func,
    errorMessage: PropTypes.func,
    serverSave: PropTypes.func,
    serverShow: PropTypes.func
  }).isRequired,
  location: PropTypes.shape({}).isRequired,
  login: PropTypes.shape({
    user: PropTypes.shape({
      user_id: PropTypes.string
    })
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
      section: PropTypes.string
    }).isRequired
  }).isRequired
};
export { ContractSpec as TestContractSpec };
export default entityComposition(ContractSpec, ['contractSpec', 'job', 'login', 'contractSpecFilterContractSpec'], {
  canView: state => viewPermissionSelector('job')(state),
  canEdit: state => editPermissionSelector('job')(state),
  jobAndPhaseCombinations: state => state.contractSpecFilterContractSpec.list,
  jobAndPhaseInitialIndex: state => state.contractSpecFilterContractSpec.initialIndexQuery
});